import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { FaGithub } from 'react-icons/fa'
import { VscCode } from 'react-icons/vsc'
import Img from "gatsby-image"
import "./ProjectItemStyles.scss";
import { useState, useEffect } from 'react';
import CursorContext, { CursorLookType } from '../CustomCursor/context/CursorContext';
import { useContext } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import { motion, useCycle } from 'framer-motion';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const GalleryItem = ({src, onClick, name, desc}) => {
  const { setType } = useContext(CursorContext);
  const [reveal, setReveal] = useState(false);
  let imgRef;

  const [scrollPosition, setScrollPosition] = useState(0);
  const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });


    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const viewportOffset = imgRef.getBoundingClientRect();
    const top = viewportOffset.top;
    const height = imgRef.clientHeight

    //console.log(top - window.innerHeight, height)

    if (top - window.innerHeight <= -height/5) {
        setReveal(true);
    }
  }, [scrollPosition]);

  const [animate, cycle] = useCycle(
    {
      zIndex: 1,
      opacity: 1,
    },
    {
      zIndex: -1,
      opacity: 0,
      y: "-80%",
    }
  );

  const handleClick = () => {
    cycle();
    onClick();
  }

  return (
    <div 
      ref={el => imgRef = el}
      className={`gallery-item-wrapper ${reveal ? 'is-reveal' : ''}`}
    >
      <div 
        onClick={() => handleClick()} 
        className='gallery-item'
      >
        <div 
          onMouseEnter={() => setType(CursorLookType.MENU)}
          onMouseLeave={() => setType(CursorLookType.DEFAULT)}
          className="img-wrapper"
        >
          <Img style={{ maxHeight: '40em' }} fluid={src} className="gallery-item-img sepia"/>
        </div>
      </div>
      <motion.div 
        className="project-card"
        animate={animate}
        transition={{ ease: "easeInOut", duration: 0.8 }}
      >
          <div className="title">
            <h3>{name}</h3>
            <div className="arr">
              <BsArrowRight />
            </div>
          </div>
          <span className="project-info">{desc}</span>
      </motion.div>
    </div>
  );
}


const ProjectItem = ({proj}: any) => {
    const data = useStaticQuery(graphql`
    query {
      harmonic: file(relativePath: { eq: "harmonic.png" }) {
        childImageSharp {
          fluid(maxWidth: 3080, quality: 100) {
            ...GatsbyImageSharpFluid
          }
          original {
            width
          }
        }
      },
      rplss: file(relativePath: { eq: "rplss.png" }) {
        childImageSharp {
          fluid(maxWidth: 3080, quality: 100) {
            ...GatsbyImageSharpFluid
          }
          original {
            width
          }
        }
      },
      algo: file(relativePath: { eq: "algo.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 3080, quality: 100) {
            ...GatsbyImageSharpFluid
          }
          original {
            width
          }
        }
      },
      illumi: file(relativePath: { eq: "illumi.png" }) {
        childImageSharp {
          fluid(maxWidth: 3080, quality: 100) {
            ...GatsbyImageSharpFluid
          }
          original {
            width
          }
        }
      },
      ecri: file(relativePath: { eq: "ecri.png" }) {
        childImageSharp {
          fluid(maxWidth: 3080, quality: 100) {
            ...GatsbyImageSharpFluid
          }
          original {
            width
          }
        }
      },
      stockpreme: file(relativePath: { eq: "stockpreme.png" }) {
        childImageSharp {
          fluid(maxWidth: 3080, quality: 100) {
            ...GatsbyImageSharpFluid
          }
          original {
            width
          }
        }
      }
    }
  `);

    const getSrc = (name: string) => {
      switch(name.toLowerCase()) {
        case "harmonic": return data.harmonic.childImageSharp.fluid;
        case "rplss": return data.rplss.childImageSharp.fluid;
        case "algovis": return data.algo.childImageSharp.fluid;
        case "illumi": return data.illumi.childImageSharp.fluid;
        case "ecri": return data.ecri.childImageSharp.fluid;
        case "stockpreme": return data.stockpreme.childImageSharp.fluid;
        default: return null;
      }
    }

    const isColumn = useMediaQuery("(max-width: 1200px)");
    const [state, setState] = useState(true);
    const from = {
      collapse: isColumn ? { left: "-80%", height: 0 } : { left: "-80%", width: 0},
      collapseChildren: isColumn ? { opacity: 0, y: "40%", height: 0, overflow: "hidden", transition: { overflow: { delay: 0.8 } } } : { opacity: 0 }
    }
    const to = {
      collapse: isColumn ? { left: "0%", height: "100%" } : { left: "0%", width: "auto"},
      collapseChildren: isColumn ? { opacity: 1, y: "-10%" } : { opacity: 1 }
    }

    return (
      <div className="project-item-wrapper">
        <div style={{ alignItems: 'center' }} className="project-item">
            <GalleryItem name={proj.name} desc={proj.description} onClick={() => setState(!state)} src={getSrc(proj.name)} />
            <motion.div
              className='description'
              animate={state ? from.collapse : to.collapse}
              transition={{ ease: "easeInOut", duration: 0.75 }}              
            >
                <motion.p
                  animate={state ? from.collapseChildren : to.collapseChildren}
                  transition={{ ease: "easeInOut", duration: 0.75 }}
                >
                    <h1>{proj.name}</h1>
                    <small>Project Overview</small>
                    <br />
                    {proj.p1}
                    <br />
                    <br />
                    {proj.p2}
                    <br />
                    <br />
                    <small>Tech Implemented</small>
                    <ul>
                        {proj.tech.map(tech => (
                          <li>{tech}</li>
                        ))
                        }
                    </ul>
                    <div className="actions">
                        {
                          proj.name.toLowerCase() === 'algovis'&& (
                            <>
                              <div className="repo" >
                                <a className="hover-underline-animation" href={proj.repoPath} target="_blank"><FaGithub /> View Path Repo</a>
                                <a className="hover-underline-animation" href={proj.repoSort} target="_blank"><FaGithub /> View Sort Repo</a> 
                              </div>
                              <div className="live" >
                                <a className="hover-underline-animation" href={proj.livePath} target="_blank"><VscCode /> Path Live </a>
                                <a className="hover-underline-animation" href={proj.liveSort} target="_blank"><VscCode /> Sort Live </a> 
                              </div>
                            </>
                          )
                        }
                        { proj.repo && <a className="hover-underline-animation" href={proj.repo} target="_blank"><FaGithub /> View Repo</a> }
                        { proj.live && <a className="hover-underline-animation" href={proj.live} target="_blank"><VscCode /> Live </a> }
                    </div>
                </motion.p>
            </motion.div>
        </div>
      </div>
        
    );
};

export default ProjectItem;